import { FlexRowSmallSpaced } from '@fox/layout';
import React from 'react';
import { useAutoDataGridContext } from '../../auto-data-grid/context';
import { defaultColumnActionMenu } from '../settings-menu';
import { HIDE_ACTION_KEY, PIN_ACTION_KEY } from '../settings-menu/column-menu-options';
import { GraphColumn } from '../types';

import { ActionMenuPopover, ActionsMenuOption, HoverInfoIcon, IconButton, Popover } from '@fox/atoms';
import { ColumnHeaderComp } from '../../data-grid-table/components/column-header';
import { useDataGridContext } from '../../data-grid-table/context';

import styled from 'styled-components';
import { FilterPopoverInner } from '../../grid-toolbar/filter/filter-popover';

const MainTitleWrapper = styled.div`
	width: 100%;
	display: grid;
	grid-template: 1fr/1fr auto auto;
	align-items: center;
	transition: all 0.1s ease-in-out;

	.tableTitle {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		margin-right: 0.2rem;
		width: 100%;
	}

	.hiddenMenu {
		width: 0;
		visibility: hidden;
		margin-left: auto;
	}

	&:hover,
	&:focus,
	&:active {
		.hiddenMenu {
			width: 100%;
			visibility: visible;
		}
	}

	.popupIcon {
		transition: all 0.1s ease-in-out;
		&:hover,
		&:focus {
			transform: scale(1.1) translateY(-3px);
		}
		&:active {
			transform: scale(1) translateY(0px);
		}
	}
`;

export function AutoColumnTitle<T>(props: { col: GraphColumn<T>; hideToolbar: boolean; buildOptionsMenu: (col: GraphColumn<T>) => ActionsMenuOption[]; hidePinAndHide?: boolean }) {
	const { filters, setFilters, pinnedColumnsDataIndex, setPinnedColumnsDataIndex, sort, setSortColumn, localSaveOnly } = useAutoDataGridContext();
	const dragContext = useDataGridContext();

	const { col, hideToolbar, buildOptionsMenu } = props;

	let columnOptions;

	if (buildOptionsMenu) {
		columnOptions = buildOptionsMenu(col);
	} else {
		columnOptions = defaultColumnActionMenu(col, hideToolbar);
	}
	// remove pin and hide
	// TODO: This seems a bit brittle
	if (props.hidePinAndHide) {
		columnOptions = columnOptions.filter(o => o.key !== PIN_ACTION_KEY && o.key !== HIDE_ACTION_KEY);
	}

	// HACK: This is a band-aid around the fact that most usages of the one-table-to-rule-them-all component define sortable
	// columns (sortable: true), but do not specify the sortIndex property. This would normally be fine for most
	// cases, except the List Builder results table where sortIndex is different from dataIndex and this distinction
	// needs to be made (ie. We need to sort on the column indicated by sortIndex, not dataIndex!)
	//
	// To cater for both use cases, we'll assume sortIndex as "the index" and only fall back to dataIndex iff the column
	// does not specify sortIndex and (sortable == true)
	//
	// The true fix is to kill the sortable property, and infer sortability through whether sortIndex is present or not. This
	// fix is very disruptive due to everything that isn't List Builder defining sortable columns completely wrong, so such
	// a fix can be postponed for later.
	let sortIndex = col.sortIndex;
	if ((!sortIndex && col.sortable) || localSaveOnly) {
		sortIndex = col.dataIndex;
	}

	const canDrag = dragContext.onColumnOrderChanged !== undefined && dragContext.onColumnResized !== undefined;
	const TitleWrap = col.titleWrap ? col.titleWrap : React.Fragment;

	const hasFiltersApplied = filters?.find(filter => filter.dataIndex === col.dataIndex) !== undefined;
	const isPinned = pinnedColumnsDataIndex.includes(col.dataIndex);
	const hasSortApplied = sort && sort.column === sortIndex;

	let upperCaseTitle;
	upperCaseTitle = col.title;
	if (typeof col.title === 'string' || col.title instanceof String) {
		upperCaseTitle = col.title[0]?.toUpperCase() + col.title.slice(1);
	}

	return (
		<TitleWrap {...(col.titleWrap ? col.titleWrapProps : undefined)}>
			<ColumnHeaderComp
				canDrag={col.fixed !== 'left' && (col.canDrag ?? canDrag)}
				contents={
					<>
						<MainTitleWrapper>
							<FlexRowSmallSpaced>
								<div className="tableTitle" title={upperCaseTitle?.toUpperCase?.()}>
									{upperCaseTitle}
								</div>
								{col.description ? (
									<div style={{ cursor: 'default' }}>
										<HoverInfoIcon infoText={col.description} />
									</div>
								) : (
									<></>
								)}
							</FlexRowSmallSpaced>

							<div style={{ display: 'flex', alignItems: 'center' }}>
								{hasFiltersApplied && (
									<Popover trigger="click" overlay={<FilterPopoverInner columns={col} />}>
										<div className="popupIcon">
											<IconButton size="small" iconName="FilterAlt" title="Show filters" />
										</div>
									</Popover>
								)}
								{isPinned && (
									<div className="popupIcon">
										<IconButton
											size="small"
											iconName="PushPin"
											title="Remove pin"
											onClick={() => {
												setPinnedColumnsDataIndex([...pinnedColumnsDataIndex.filter(index => index !== sortIndex)]);
											}}
										/>
									</div>
								)}
								{hasSortApplied && sort.asc && (
									<div className="popupIcon">
										<IconButton
											size="small"
											iconName="Sort"
											title="Sort descending"
											style={{ transform: 'rotate(180deg) scaleX(-1)' }}
											onClick={() => {
												setSortColumn({ column: sortIndex, asc: false });
											}}
										/>
									</div>
								)}
								{hasSortApplied && !sort.asc && (
									<div className="popupIcon">
										<IconButton
											size="small"
											iconName="Sort"
											title="Sort ascending"
											onClick={() => {
												setSortColumn({ column: sortIndex, asc: true });
											}}
										/>
									</div>
								)}
							</div>
							<div className="hiddenMenu">
								<ActionMenuPopover options={columnOptions} />
							</div>
						</MainTitleWrapper>
					</>
				}
			/>
		</TitleWrap>
	);
}
